/**
 * Sharing one of your own calendars with a colleague (agora#158, MSG-055's
 * last slice): resolve their address to a principal with `resolvePrincipal`,
 * grant an access level -- plus, since the owner's 2026-09-27 decision,
 * editing and re-sharing rights ticked on top of it -- with `shareCalendar`,
 * and let the owner revoke a grant later with `unshareCalendar`. Agora draws
 * its own dialog here rather than reusing `@wtfalch/calendar/calendar/react`'s
 * own `ShareDialog` -- `Calendar.tsx`'s own docblock: "Agora draws every
 * screen itself now".
 *
 * `ShareCalendar` is the row-level control `Week.tsx`'s calendar list
 * renders per calendar: nothing at all when `myRights` carries no
 * `mayShare` (only a calendar's owner, or someone shared with at that
 * right, may share it further), a plain "Share" trigger otherwise, and
 * `ShareCalendarDialog` once pressed.
 */
import type {
  CalendarClient,
  Calendar as CalendarModel,
  CalendarRight,
  Principal,
} from '@wtfalch/calendar/calendar';
import { CalendarError } from '@wtfalch/calendar/calendar';
import { Button, Checkbox, Field, Input, Modal, Select } from '@wtfalch/design';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** The two levels `shareCalendar`'s `ShareGrant.rights` can carry here, the
 *  same two -- and the same labels -- `@wtfalch/calendar/calendar/react`'s
 *  own `ShareDialog` offers. A write level was, until now, not a third entry
 *  beside them: the SDK's own `DESIGN.md` called adding one "a product
 *  decision, not a dropdown entry". The owner has made that decision
 *  (2026-09-27): editing and re-sharing are extra rights a grant carries on
 *  top of a level, both off by default, not levels of their own -- see the
 *  "Can edit events" and "Can share with others" `Checkbox`es below, and
 *  `WRITE_RIGHTS`/`SHARE_RIGHT`. No live test yet proves a second account
 *  can actually write under `mayWriteAll` against a running server. */
const LEVELS = [
  { value: 'busy', label: 'Busy times', rights: ['mayReadFreeBusy'] },
  { value: 'events', label: 'Events', rights: ['mayReadFreeBusy', 'mayReadItems'] },
] as const satisfies readonly { value: string; label: string; rights: readonly CalendarRight[] }[];
type LevelValue = (typeof LEVELS)[number]['value'];
const DEFAULT_LEVEL = LEVELS[1];

/** What "Can edit events" adds on top of a level. Stalwart's own JMAP layer
 *  already folds creating, changing and deleting events into `mayWriteAll`
 *  (`Acl::Modify`, `AddItems`, `ModifyItems`, `RemoveItems` --
 *  `crates/jmap-proto/src/object/calendar.rs:402-419`,
 *  `crates/jmap/src/calendar_event/set.rs:118`), and the JMAP Calendars
 *  draft has `mayWriteAll` imply `mayWriteOwn`, `mayUpdatePrivate` and
 *  `mayRSVP`, so those three ride along for a grant that reads them back
 *  explicitly. `mayDelete` is left out on purpose: on the wire it is
 *  `Acl::Delete`, which is the right to delete the *calendar itself*, not
 *  an event on it (`crates/dav/src/calendar/delete.rs:119-124`) -- an
 *  over-grant "Can edit events" never promised. Only makes sense with the
 *  Events level -- `mayReadItems` is what lets a person see an event to
 *  edit it -- so the dialog disables and clears this checkbox under Busy
 *  times, and hides it entirely when the sharer's own `myRights` lacks
 *  `mayWriteAll` (a re-share can't hand out a right it does not hold). */
const WRITE_RIGHTS = [
  'mayWriteAll',
  'mayWriteOwn',
  'mayUpdatePrivate',
  'mayRSVP',
] as const satisfies readonly CalendarRight[];
/** What "Can share with others" adds: the same right that shows or hides
 *  the row-level "Share" trigger itself (`ShareCalendar`, below). */
const SHARE_RIGHT: CalendarRight = 'mayShare';

/** Which level a grant already on the server reads as -- `mayReadItems` is
 *  the one right that separates them; any grant without it is the quieter
 *  one. */
function levelOf(rights: readonly CalendarRight[]): (typeof LEVELS)[number] {
  return rights.includes('mayReadItems') ? LEVELS[1] : LEVELS[0];
}

/** What else a grant carries beyond its level, in the words the existing-
 *  shares list shows: "can edit" for the write rights, "can share" for
 *  `mayShare`. Order matches the two checkboxes. */
function extrasOf(rights: readonly CalendarRight[]): string[] {
  const extras: string[] = [];
  if (rights.includes('mayWriteAll')) extras.push('can edit');
  if (rights.includes(SHARE_RIGHT)) extras.push('can share');
  return extras;
}

/** The server's own words when it has them: `CalendarError` carries the
 *  server's description, which says more than "something went wrong". */
function messageOf(error: unknown): string {
  return error instanceof CalendarError && error.message !== ''
    ? error.message
    : 'The server refused that, and nothing changed.';
}

type Row = {
  accountId: string;
  rights: readonly CalendarRight[];
  principal: Principal | undefined;
};

export function ShareCalendarDialog({
  client,
  calendar,
  onClose,
}: {
  client: CalendarClient;
  calendar: CalendarModel;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [address, setAddress] = useState('');
  // A sharer can only hand out what their own grant on this calendar
  // carries -- a re-share can't mint a right it does not hold itself. The
  // server does not check this (Stalwart's acl_validate bounds neither the
  // level nor the extras by the granter's own rights), so this dialog must.
  const levels = useMemo(
    () => LEVELS.filter((one) => one.rights.every((r) => calendar.myRights.includes(r))),
    [calendar.myRights],
  );
  const startLevel =
    levels.find((one) => one.value === DEFAULT_LEVEL.value) ?? levels[0] ?? LEVELS[0];
  const [level, setLevel] = useState<LevelValue>(startLevel.value);
  const [canEdit, setCanEdit] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [ownAccountId, setOwnAccountId] = useState<string | null>(null);
  const mayGrantEdit = calendar.myRights.includes('mayWriteAll');
  const mayGrantShare = calendar.myRights.includes(SHARE_RIGHT);

  // Re-read the grants back from the server, then put a name and an
  // address on each one -- a grant carries only an account id, which is
  // not a thing to show a person. Never optimistic: a write below re-reads
  // rather than patching local state, so a refused write leaves the person
  // looking at what is actually granted.
  const read = useCallback(async () => {
    const calendars = await client.listCalendars();
    const grants = calendars.find((c) => c.id === calendar.id)?.shareWith ?? [];
    const principals = await client.lookupPrincipals(grants.map((g) => g.accountId));
    const byId = new Map(principals.map((p) => [p.accountId, p]));
    setRows(
      grants.map((g) => ({
        accountId: g.accountId,
        rights: g.rights,
        principal: byId.get(g.accountId),
      })),
    );
  }, [client, calendar.id]);

  useEffect(() => {
    read().catch((err: unknown) => setFailure(messageOf(err)));
  }, [read]);

  // For `share`'s own "That is you" check below -- silent on failure, since
  // its only job is a friendlier message than the server's own refusal.
  useEffect(() => {
    client
      .identity()
      .then((id) => setOwnAccountId(id.accountId))
      .catch(() => {});
  }, [client]);

  const share = useCallback(async () => {
    const typed = address.trim();
    if (typed === '' || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const principal = await client.resolvePrincipal(typed);
      if (!principal) {
        // Not an error from the server's point of view, and not one to
        // this person either: they mistyped, or that colleague is on
        // another server. Say which so the next attempt is better
        // informed.
        setFailure(`Nobody on this server has the address ${typed}.`);
        return;
      }
      if (principal.accountId === ownAccountId) {
        // The server's own refusal for granting yourself an ACL on your
        // own calendar is not a thing to show a person as-is.
        setFailure('That is you.');
        return;
      }
      const levelRights = (levels.find((one) => one.value === level) ?? startLevel).rights;
      const rights: CalendarRight[] = [
        ...levelRights,
        ...(canEdit ? WRITE_RIGHTS : []),
        ...(canShare ? [SHARE_RIGHT] : []),
      ];
      await client.shareCalendar(calendar.id, { accountId: principal.accountId, rights });
      await read();
      setAddress('');
      // Back to the defaults, so the next colleague this dialog shares with
      // starts from "Events, nothing extra" rather than carrying over
      // whatever the last one was granted.
      setLevel(startLevel.value);
      setCanEdit(false);
      setCanShare(false);
    } catch (err) {
      setFailure(messageOf(err));
    } finally {
      setBusy(false);
    }
  }, [
    address,
    busy,
    client,
    calendar.id,
    level,
    canEdit,
    canShare,
    read,
    ownAccountId,
    levels,
    startLevel,
  ]);

  const remove = useCallback(
    async (accountId: string) => {
      if (busy) return;
      setBusy(true);
      setFailure(null);
      try {
        await client.unshareCalendar(calendar.id, accountId);
        await read();
      } catch (err) {
        setFailure(messageOf(err));
      } finally {
        setBusy(false);
      }
    },
    [busy, client, calendar.id, read],
  );

  return (
    <Modal
      title={`Share "${calendar.name}"`}
      onClose={busy ? undefined : onClose}
      closeDisabled={busy}
      footer={
        <>
          <Button kind="ghost" onPress={onClose} disabled={busy}>
            Done
          </Button>
          <Button
            kind="primary"
            busy={busy}
            disabled={address.trim() === ''}
            onPress={() => void share()}
          >
            Share
          </Button>
        </>
      }
    >
      <div className="cal-find-form">
        <div>
          <span className="up">Who has access</span>
          {rows === null ? (
            <p className="text-xs text-muted">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-muted">
              Nobody yet. This calendar is yours alone until you share it.
            </p>
          ) : (
            <ul className="flex gap-2 flex-wrap cal-find-chips">
              {rows.map((row) => (
                <li key={row.accountId} className="flex items-center gap-2 cal-share-row">
                  <span className="text-sm">{row.principal?.email ?? row.accountId}</span>
                  <span className="text-xs text-muted">
                    {[levelOf(row.rights).label, ...extrasOf(row.rights)].join(', ')}
                  </span>
                  <Button
                    kind="ghost"
                    size="sm"
                    disabled={busy}
                    aria-label={`Stop sharing with ${row.principal?.email ?? row.accountId}`}
                    onPress={() => void remove(row.accountId)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex gap-4 flex-wrap cal-find-row">
          <Field label="Share with" hint="Their calendar address, on this server.">
            {(field) => (
              <Input
                {...field}
                type="email"
                block
                value={address}
                disabled={busy}
                placeholder="someone@example.org"
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void share();
                  }
                }}
              />
            )}
          </Field>
          <Field
            label="They can see"
            hint="Busy times says when you are free and nothing else. Events says what the meetings are."
          >
            <Select
              value={level}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.value as LevelValue;
                setLevel(next);
                if (next === 'busy') setCanEdit(false);
              }}
              block
            >
              {levels.map((one) => (
                <option key={one.value} value={one.value}>
                  {one.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {(mayGrantEdit || mayGrantShare) && (
          <div className="flex gap-4 flex-wrap cal-find-row">
            {mayGrantEdit && (
              <Checkbox
                label="Can edit events"
                hint="They can create, change and delete events, not just view them."
                checked={canEdit}
                disabled={busy || level === 'busy'}
                onChange={setCanEdit}
              />
            )}
            {mayGrantShare && (
              <Checkbox
                label="Can share with others"
                hint="They can share this calendar with people you have not shared it with."
                checked={canShare}
                disabled={busy}
                onChange={setCanShare}
              />
            )}
          </div>
        )}
        {failure && (
          <p className="cal-error text-bad" role="alert">
            {failure}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** The row-level control: nothing when this calendar's own rights carry no
 *  `mayShare`, a plain "Share" trigger otherwise. `Week.tsx` renders this as
 *  a sibling *after* the row's own `<label>`, never inside it -- a `<button>`
 *  is itself a labelable element, so one placed before the checkbox inside
 *  that label would make the browser's implicit label association target
 *  the button instead of the checkbox, and a click on the calendar's name
 *  would open Share rather than toggle its visibility (agora#158 review
 *  finding). */
export function ShareCalendar({
  client,
  calendar,
}: {
  client: CalendarClient;
  calendar: CalendarModel;
}) {
  const [open, setOpen] = useState(false);
  if (!calendar.myRights.includes('mayShare')) return null;
  return (
    <>
      <button
        type="button"
        className="cal-share-btn"
        aria-label={`Share ${calendar.name}`}
        onClick={() => setOpen(true)}
      >
        Share
      </button>
      {open && (
        <ShareCalendarDialog client={client} calendar={calendar} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
